import React, { useRef, useState } from "react";

import { Box, CircularProgress, Typography } from "@mui/material";
import { toast } from "@rubi/ui";
import { useTranslation } from "react-i18next";

import { acceptsFile } from "./acceptsFile";

/**
 * An upload target. Click to browse, drop onto it, or paste into it — all three on both variants,
 * whatever the labels say.
 *
 * `variant="tri"` is the design's three-box row, the same shape as the old flow's `TriUploadFile`:
 * the boxes label the three routes rather than each owning one, because a drop lands wherever it is
 * released and a paste goes to whatever has focus. `variant="row"` is the compact single line the
 * bulletin PDF uses — one file, and not the point of this section.
 *
 * The zone owns the `accept` filter, so a file the wrong side of it never reaches the caller. The
 * browser applies `accept` to the picker only; drops and pastes would otherwise be unfiltered.
 */

interface MediaDropZoneProps {
  variant: "tri" | "row";
  /** `row` only — the tri-box labels its three routes instead. */
  icon?: React.ReactNode;
  title: string;
  hint?: string;
  /** Standard input `accept` — also what drops and pastes are filtered against. */
  accept: string;
  multiple?: boolean;
  disabled?: boolean;
  busy?: boolean;
  onFiles: (files: File[]) => void;
}

export const MediaDropZone: React.FC<MediaDropZoneProps> = ({
  variant,
  icon,
  title,
  hint,
  accept,
  multiple = false,
  disabled = false,
  busy = false,
  onFiles,
}) => {
  const { t } = useTranslation("bulletin/create-v2");
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const browse = () => {
    if (!disabled) input.current?.click();
  };

  const submit = (files: FileList | null) => {
    const all = Array.from(files ?? []);
    const accepted = all.filter((file) => acceptsFile(file, accept));

    if (accepted.length < all.length) toast.error(t("media.error_file_type"));
    if (accepted.length === 0) return;

    onFiles(multiple ? accepted : accepted.slice(0, 1));
  };

  /** One box of the tri-row. Only the first one takes the click; the rest are labels. */
  const box = (
    label: React.ReactNode,
    {
      dashed = false,
      phone = true,
      onClick,
    }: { dashed?: boolean; phone?: boolean; onClick?: () => void } = {},
  ) => (
    <Box
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick && !disabled ? 0 : undefined}
      onKeyDown={(event) => {
        if (!onClick) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
      sx={{
        flex: 1,
        // A phone has neither of the other two gestures, and three boxes across do not fit.
        display: phone ? "flex" : { xs: "none", sm: "flex" },
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        px: 1.5,
        py: 1.75,
        border: 2,
        borderStyle: dashed ? "dashed" : "solid",
        borderColor: dragging && dashed ? "primary.main" : "text.secondary",
        borderRadius: 1,
        color: "text.secondary",
        textAlign: "center",
        cursor: onClick && !disabled ? "pointer" : "default",
        "&:hover":
          onClick && !disabled
            ? { backgroundColor: "action.hover", color: "text.primary" }
            : undefined,
      }}
    >
      <Typography variant="body2">{label}</Typography>
    </Box>
  );

  return (
    <Box
      // Paste needs the zone to hold focus, which is why it is reachable by tab in both variants.
      tabIndex={disabled ? -1 : 0}
      onPaste={(event) => !disabled && submit(event.clipboardData.files)}
      onDragOver={(event) => {
        if (disabled) return;
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        if (disabled) return;
        event.preventDefault();
        setDragging(false);
        submit(event.dataTransfer.files);
      }}
      sx={{
        opacity: disabled ? 0.6 : 1,
        outline: "none",
        ...(variant === "tri"
          ? { display: "flex", gap: 1 }
          : {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              px: 2,
              py: 1.75,
              border: 1,
              borderStyle: "dashed",
              borderColor: dragging ? "primary.main" : "divider",
              borderRadius: 2,
              color: "text.secondary",
              cursor: disabled ? "default" : "pointer",
              backgroundColor: dragging ? "action.hover" : undefined,
              "&:hover": disabled
                ? undefined
                : { backgroundColor: "action.hover" },
            }),
      }}
      {...(variant === "row"
        ? {
            role: "button",
            "aria-label": title,
            onClick: browse,
            onKeyDown: (event: React.KeyboardEvent) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                browse();
              }
            },
          }
        : {})}
    >
      {variant === "tri" ? (
        <>
          {box(
            busy ? (
              <CircularProgress size={18} />
            ) : (
              t("media.upload_from_device")
            ),
            { onClick: browse },
          )}
          {box(t("media.paste_hint"), { phone: false })}
          {box(t("media.drag_and_drop"), { dashed: true, phone: false })}
        </>
      ) : (
        <>
          {busy ? <CircularProgress size={20} /> : icon}
          <Typography variant="body2">{title}</Typography>
          {hint && <Typography variant="caption">{hint}</Typography>}
        </>
      )}

      <input
        ref={input}
        type="file"
        aria-label={title}
        accept={accept}
        multiple={multiple}
        style={{ display: "none" }}
        onChange={(event) => {
          submit(event.target.files);
          // Cleared so re-picking the same file fires `change` again.
          event.target.value = "";
        }}
      />
    </Box>
  );
};
